import { useTranslate } from '@tolgee/react';
import { MenuButton } from './MenuButton';
import { UserOutlined } from '@ant-design/icons';
import {
  AccountFormModal,
  AccountFormValues,
  AccountFormValuesV2,
  AccountPasswordForm,
} from 'views/Assistant/components';
import useModal from 'hooks/useModal';
import { useIsTrail } from 'views/Assistant/Account';
import { isNil, pick, pickBy } from 'lodash-es';
import { queryAssistant, update as updateAccount } from 'services/api/assistant.account';
import { useAuthorization } from 'components/Authorization';
import { Alert, Button, message } from 'antd';
import serverMsg from 'utils/serverMsg';
import { useCallback, useEffect, useState } from 'react';
import { getLocalTimeZone } from 'utils/timeZone';
import useIsPasswordSet from 'hooks/business/user/useIsPasswordSet';
import { JustRightHeightModal } from 'components/styled/scoped';
import { updateUserInfo } from 'services/api/account';
import useLayerLock from 'hooks/useLayerLock';
import { useTransAppProductName } from 'hooks/useTrans';
import { isSiteGiant } from 'productLine';

interface AccountInfoProps {
  hidePopover: () => void;
}

export const AccountInfo = (props: AccountInfoProps) => {
  const { hidePopover } = props;
  const { t } = useTranslate();
  const accountModal = useModal<AccountFormValues>();
  const isTrail = useIsTrail();
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const handleClick = useCallback(() => {
    if (!payload) return;

    hidePopover();
    const { id, account, phoneNumber, type } = payload;

    queryAssistant({
      user_ids: [`${id}`],
    }).then((res) => {
      const [data] = res;
      accountModal.show({
        id,
        name: data.name ?? '',
        email: data.email ?? '',
        account: account ?? '',
        password: '',
        phoneNumber: phoneNumber ?? '',
        type: type ?? 1,
        timeZone: data.timeZone ?? getLocalTimeZone(),
        incomingLineLimit: data.incomingLineLimit || 50,
        bindLogins: data.bindLogins,
      });
    });
  }, [accountModal, payload, hidePopover]);

  const handleSubmit = useCallback(
    (v: AccountFormValuesV2) => {
      const values = pickBy(
        pick(v as any, ['email', 'name', 'timeZone', 'incomingLineLimit']),
        (it: any) => !isNil(it),
      ) as any;
      return updateAccount({
        id: `${payload?.id}`,
        ...values,
        phoneNumber: payload?.phoneNumber as string,
      })
        .then(() => {
          message.success(t('account.updateSucceed'));
          accountModal.hide();
        })
        .catch((err) => {
          serverMsg(err)
            .then(message.error)
            .catch(() => {
              message.error(t('account.updateFailed'));
            });
        });
    },
    [t, accountModal, payload?.id, payload?.phoneNumber],
  );

  return (
    <>
      <MenuButton
        label={t('status.帐号信息')}
        customIcon={<UserOutlined />}
        onClick={handleClick}
      />
      <AccountFormModal
        destroyOnClose
        initialValues={accountModal.meta}
        visible={accountModal.visible}
        isTrail={isTrail}
        hiddenFields={['roleInDepartments']}
        onCancel={() => accountModal.hide()}
        onSubmit={handleSubmit}
        showAuth2
      />
    </>
  );
};

const HAS_SET_PASSWORD_STORAGE_KEY = 'fett/hasSetPassword';

export const NotSetPasswordAlert = () => {
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { id } = payload || {};
  const isPwdSet = useIsPasswordSet();
  const { gained, release, acquire } = useLayerLock('setPwdInfo', 99998);
  const [visible, setVisible] = useState(() => {
    return localStorage.getItem(HAS_SET_PASSWORD_STORAGE_KEY) === null;
  });
  const appProductNameT = useTransAppProductName();
  const actualVisible = !isPwdSet && visible;
  useEffect(() => {
    if (!actualVisible) {
      release();
      return () => {};
    }
    acquire();
    return () => {
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualVisible]);
  const { t } = useTranslate();
  return isPwdSet ? null : (
    <JustRightHeightModal
      visible={visible && gained}
      centered
      onCancel={() => {
        setVisible(false);
        localStorage.setItem(HAS_SET_PASSWORD_STORAGE_KEY, 'set');
      }}
      title={t('modals.设置帐号密码')}
      footer={null}
      maskClosable={false}
    >
      {isSiteGiant && (
        <Alert
          className="!mb-[12px]"
          message={appProductNameT(
            '设置密码后可以通过登录页面登录「产品名称」，你也可以前往【账号信息】完成设置',
          )}
          type="warning"
          showIcon
        />
      )}
      <AccountPasswordForm
        initialValues={{
          id: `${id}`,
          account: '',
          oldPassword: '',
          newPassword: '',
          confirmPassword: '',
        }}
        compact
        onSubmit={(values) => {
          const { newPassword, oldPassword } = pick(values, ['newPassword', 'oldPassword']);
          updateUserInfo({
            oldPassword,
            newPassword,
          }).then(() => {
            message.success(t('account_update_pwd_success_msg'));
            setVisible(false);
            localStorage.setItem(HAS_SET_PASSWORD_STORAGE_KEY, 'set');
          });
        }}
      >
        {(form, formikHelper) => {
          return (
            <>
              {form}
              <Button
                disabled={!formikHelper.dirty || !formikHelper.isValid}
                loading={formikHelper.isSubmitting}
                type="primary"
                block
                onClick={formikHelper.submitForm}
              >
                {t('modals.确认修改')}
              </Button>
            </>
          );
        }}
      </AccountPasswordForm>
    </JustRightHeightModal>
  );
};
